// frontend/src/components/Portfolio/PercentileRankings.tsx
// UPDATED: Added composite risk metric + filter zero percentiles + all 18 hazards

import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

interface PortfolioItem {
  postal_code?: string;
  zip_code?: string;
  city?: string;
  state?: string;
  risk_score?: number;
  total_damage_10?: number;
  total_event_count_10?: number;
  // All 18 hazards
  avalanche?: number;
  cold?: number;
  drought?: number;
  earthquake?: number;
  flood?: number;
  flood_r?: number;
  hail?: number;
  heat?: number;
  hurricane?: number;
  ice?: number;
  landslide?: number;
  lightning?: number;
  tornado?: number;
  tsunami?: number;
  volcanic?: number;
  wildfire?: number;
  wind?: number;
  winter?: number;
  [key: string]: any;
}

interface PercentileRankingsProps {
  portfolio: {
    items: PortfolioItem[];
    [key: string]: any;
  };
}

interface RankedItem extends PortfolioItem {
  percentiles: { [key: string]: number };
}

// All 18 hazard keys for composite calculation
const HAZARD_KEYS = [
  'avalanche', 'cold', 'drought', 'earthquake', 'flood', 'flood_r', 
  'hail', 'heat', 'hurricane', 'ice', 'landslide', 'lightning',
  'tornado', 'tsunami', 'volcanic', 'wildfire', 'wind', 'winter'
];

const RISK_METRICS = [
  // NEW: Overall composite metric that combines all hazards
  { key: 'composite_risk', label: 'Overall Risk (All Hazards)', format: 'composite' },
  
  // Overall Metrics
  //{ key: 'risk_score', label: 'Overall Risk Score', format: 'number' },
  //{ key: 'total_damage_10', label: 'Total Damage (10yr)', format: 'currency' },
  //{ key: 'total_event_count_10', label: 'Event Count (10yr)', format: 'number' },
  
  // All 18 Climate Hazards (alphabetically organized)
  { key: 'avalanche', label: 'Avalanche Risk', format: 'number' },
  { key: 'cold', label: 'Cold Wave Risk', format: 'number' },
  { key: 'drought', label: 'Drought Risk', format: 'number' },
  { key: 'earthquake', label: 'Earthquake Risk', format: 'number' },
  { key: 'flood', label: 'Coastal Flood Risk', format: 'number' },
  { key: 'flood_r', label: 'River Flood Risk', format: 'number' },
  { key: 'hail', label: 'Hail Risk', format: 'number' },
  { key: 'heat', label: 'Heat Wave Risk', format: 'number' },
  { key: 'hurricane', label: 'Hurricane Risk', format: 'number' },
  { key: 'ice', label: 'Ice Storm Risk', format: 'number' },
  { key: 'landslide', label: 'Landslide Risk', format: 'number' },
  { key: 'lightning', label: 'Lightning Risk', format: 'number' },
  { key: 'tornado', label: 'Tornado Risk', format: 'number' },
  { key: 'tsunami', label: 'Tsunami Risk', format: 'number' },
  { key: 'volcanic', label: 'Volcanic Activity Risk', format: 'number' },
  { key: 'wildfire', label: 'Wildfire Risk', format: 'number' },
  { key: 'wind', label: 'Strong Wind Risk', format: 'number' },
  { key: 'winter', label: 'Winter Storm Risk', format: 'number' },
];

// Calculate composite risk from all 18 hazards
const calculateCompositeRisk = (item: PortfolioItem): number => {
  const hazardScores = HAZARD_KEYS
    .map(key => {
      const value = item[key];
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
      }
      return 0;
    })
    .filter(score => score > 0); // Only include hazards that exist
  
  if (hazardScores.length === 0) return 0;
  
  // Return weighted average
  return hazardScores.reduce((sum, score) => sum + score, 0) / hazardScores.length;
};

export default function PercentileRankings({ portfolio }: PercentileRankingsProps) {
  const [selectedMetric, setSelectedMetric] = useState('composite_risk');

  const calculatePercentile = (value: number, allValues: number[]): number => {
    if (isNaN(value) || allValues.length === 0) return 0;
    const sorted = [...allValues].sort((a, b) => a - b);
    const index = sorted.findIndex(v => v >= value);
    if (index === -1) return 100;
    return Math.round((index / sorted.length) * 100);
  };

  const getNumericValue = (item: PortfolioItem, key: string): number => {
    // Handle composite risk calculation
    if (key === 'composite_risk') {
      return calculateCompositeRisk(item);
    }
    
    const value = item[key];
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value.replace(/[$,]/g, ''));
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const rankedItems = useMemo(() => {
    const items = portfolio.items || [];
    
    const itemsWithPercentiles: RankedItem[] = items.map(item => {
      const rankings: { [key: string]: number } = {};
      
      RISK_METRICS.forEach(metric => {
        const values = items
          .map(i => getNumericValue(i, metric.key))
          .filter(v => !isNaN(v) && v > 0); // Filter out zero values
        
        const itemValue = getNumericValue(item, metric.key);
        rankings[metric.key] = calculatePercentile(itemValue, values);
      });

      return {
        ...item,
        percentiles: rankings,
      };
    });

    return itemsWithPercentiles.sort((a, b) => 
      (b.percentiles[selectedMetric] || 0) - (a.percentiles[selectedMetric] || 0)
    );
  }, [portfolio.items, selectedMetric]);

  const getPercentileColor = (percentile: number): string => {
    if (percentile >= 75) return '#d32f2f';
    if (percentile >= 50) return '#f57c00';
    if (percentile >= 25) return '#fbc02d';
    return '#388e3c';
  };

  const getPerformanceText = (percentile: number): string => {
    if (percentile >= 90) return 'Top 10% Highest Risk';
    if (percentile >= 75) return 'Top 25% Highest Risk';
    if (percentile >= 50) return 'Above Average Risk';
    if (percentile >= 25) return 'Below Average Risk';
    return 'Bottom 25% Lowest Risk';
  };

  const portfolioStats = useMemo(() => {
    const items = portfolio.items || [];
    const stats: { [key: string]: any } = {};

    RISK_METRICS.forEach(metric => {
      const values = items
        .map(i => getNumericValue(i, metric.key))
        .filter(v => !isNaN(v) && v > 0);

      if (values.length > 0) {
        const sorted = [...values].sort((a, b) => a - b);
        stats[metric.key] = {
          min: sorted[0],
          max: sorted[sorted.length - 1],
          avg: values.reduce((a, b) => a + b, 0) / values.length,
          median: sorted[Math.floor(sorted.length / 2)],
          p25: sorted[Math.floor(sorted.length * 0.25)],
          p75: sorted[Math.floor(sorted.length * 0.75)],
          p90: sorted[Math.floor(sorted.length * 0.90)],
        };
      }
    });

    return stats;
  }, [portfolio.items]);

  const formatValue = (value: any, metric: string): string => {
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return 'N/A';
    
    const metricInfo = RISK_METRICS.find(m => m.key === metric);
    
    if (metricInfo?.format === 'composite') {
      return num.toFixed(2);
    }
    
    if (metricInfo?.format === 'currency') {
      if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
      if (num >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
      return `$${num.toFixed(0)}`;
    }
    return num.toFixed(1);
  };

  return (
    <Box sx={{ 
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      p: 3
    }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
          Percentile Rankings
        </Typography>
        <Typography variant="body2" sx={{ color: '#666666' }}>
          Compare locations across portfolio relative to peer performance
        </Typography>
      </Box>

      {/* Metric Selector */}
      <Box sx={{ mb: 4 }}>
        <ToggleButtonGroup
          value={selectedMetric}
          exclusive
          onChange={(e, newMetric) => newMetric && setSelectedMetric(newMetric)}
          sx={{
            flexWrap: 'wrap',
            gap: 1,
            '& .MuiToggleButton-root': {
              color: '#333333',
              borderColor: '#e0e0e0',
              backgroundColor: '#ffffff',
              textTransform: 'none',
              '&.Mui-selected': {
                backgroundColor: '#0066cc',
                color: '#ffffff',
                borderColor: '#0066cc',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#0052a3',
                }
              },
              '&:hover': {
                backgroundColor: '#f5f5f5',
              }
            }
          }}
        >
          {RISK_METRICS.map(metric => (
            <ToggleButton key={metric.key} value={metric.key}>
              {metric.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Portfolio Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {portfolioStats[selectedMetric] && (
          <>
            <Grid item xs={6} md={3}>
              <Card sx={{ 
                backgroundColor: '#e8f5e9', 
                border: '1px solid #a5d6a7',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <CardContent>
                  <Typography variant="caption" sx={{ color: '#666666', fontWeight: 500 }}>
                    Minimum
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                    {formatValue(portfolioStats[selectedMetric].min, selectedMetric)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ 
                backgroundColor: '#fff3e0', 
                border: '1px solid #ffcc80',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <CardContent>
                  <Typography variant="caption" sx={{ color: '#666666', fontWeight: 500 }}>
                    Average
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#ef6c00', fontWeight: 600 }}>
                    {formatValue(portfolioStats[selectedMetric].avg, selectedMetric)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ 
                backgroundColor: '#fce4ec', 
                border: '1px solid #f8bbd0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <CardContent>
                  <Typography variant="caption" sx={{ color: '#666666', fontWeight: 500 }}>
                    75th Percentile
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#c2185b', fontWeight: 600 }}>
                    {formatValue(portfolioStats[selectedMetric].p75, selectedMetric)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ 
                backgroundColor: '#ffebee', 
                border: '1px solid #ef9a9a',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <CardContent>
                  <Typography variant="caption" sx={{ color: '#666666', fontWeight: 500 }}>
                    Maximum
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#c62828', fontWeight: 600 }}>
                    {formatValue(portfolioStats[selectedMetric].max, selectedMetric)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Rankings Table */}
      <TableContainer component={Paper} sx={{ 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0'
      }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>Rank</TableCell>
              <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>Location</TableCell>
              <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>Value</TableCell>
              <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>Percentile</TableCell>
              <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>Performance</TableCell>
              <TableCell sx={{ color: '#1a1a1a', fontWeight: 600 }}>Material Risks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rankedItems.map((item, index) => {
              const percentile = item.percentiles[selectedMetric] || 0;
              const value = getNumericValue(item, selectedMetric);
              
              return (
                <TableRow 
                  key={`${item.postal_code || item.zip_code}-${index}`}
                  sx={{ 
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  <TableCell>
                    <Chip 
                      label={`#${index + 1}`}
                      size="small"
                      sx={{ 
                        background: index < 3 ? 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)' : '#e0e0e0',
                        color: index < 3 ? '#fff' : '#333',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a1a' }}>
                      {item.city || 'Unknown'}, {item.state || ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666666' }}>
                      {item.postal_code || item.zip_code}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: '#1a1a1a', fontWeight: 500 }}>
                    {formatValue(value, selectedMetric)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flexGrow: 1, maxWidth: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={percentile}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getPercentileColor(percentile),
                              borderRadius: 4,
                            }
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#1a1a1a', minWidth: 45, fontWeight: 600 }}>
                        {percentile}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getPerformanceText(percentile)}
                      size="small"
                      sx={{
                        backgroundColor: `${getPercentileColor(percentile)}15`,
                        color: getPercentileColor(percentile),
                        fontWeight: 500,
                        border: `1px solid ${getPercentileColor(percentile)}60`
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {RISK_METRICS
                        .filter(metric => {
                          const p = item.percentiles[metric.key] || 0;
                          return p > 0; // Only show metrics with material impact
                        })
                        .map((metric) => {
                          const p = item.percentiles[metric.key] || 0;
                          return (
                            <Tooltip key={metric.key} title={`${metric.label}: ${p}%`} arrow>
                              <Chip
                                size="small"
                                label={metric.key === 'composite_risk' 
                                  ? 'All' 
                                  : metric.key === 'risk_score' 
                                    ? 'Overall' 
                                    : metric.label.split(' ')[0]}
                                sx={{
                                  backgroundColor: `${getPercentileColor(p)}20`,
                                  color: getPercentileColor(p),
                                  fontSize: '0.7rem',
                                  height: 20,
                                  border: `1px solid ${getPercentileColor(p)}60`,
                                  fontWeight: 500
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Insights */}
      <Box sx={{ mt: 4 }}>
        <Card sx={{ 
          backgroundColor: '#e3f2fd', 
          border: '1px solid #90caf9',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#0d47a1', mb: 3, fontWeight: 600 }}>
              Key Insights
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#666666', mb: 1, fontWeight: 500 }}>
                    Highest Risk Locations
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                    {rankedItems.filter(item => (item.percentiles[selectedMetric] || 0) >= 90).length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666666' }}>
                    locations in top 10%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#666666', mb: 1, fontWeight: 500 }}>
                    Above Average Risk
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                    {rankedItems.filter(item => (item.percentiles[selectedMetric] || 0) >= 50).length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666666' }}>
                    locations above median
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#666666', mb: 1, fontWeight: 500 }}>
                    Low Risk Locations
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                    {rankedItems.filter(item => (item.percentiles[selectedMetric] || 0) < 25).length}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666666' }}>
                    locations in bottom 25%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}